/**
 * Rule form <-> API payload, kept free of React so it can be unit-tested.
 */
import {
  buildExpectedValue,
  ExpectedValueType,
  expectedValueToForm,
  isCollectionTarget,
  RuleCondition,
  RuleFields,
  RulePayload,
  RuleVocabulary,
} from "../rules";

export interface RuleFormValues {
  targetEntity: string;
  fieldPath: string;
  aggregate?: string;
  conditionType: RuleCondition;
  /** Free text; split / typed by {@link buildExpectedValue} on submit. */
  expectedValue?: string;
  valueType?: ExpectedValueType;
  description?: string;
  active: boolean;
}

/** The form field names, for mapping server-side validation errors back. */
export const RULE_FORM_FIELDS = [
  "targetEntity",
  "fieldPath",
  "aggregate",
  "conditionType",
  "expectedValue",
  "description",
  "active",
];

/** What the form submits, as the API takes it. The aggregate is only sent for a collection target. */
export const toRulePayload = (
  values: RuleFormValues,
  vocabulary: Pick<RuleVocabulary, "collectionTargets"> | null | undefined
): RulePayload => {
  const aggregate = isCollectionTarget(vocabulary, values.targetEntity) ? values.aggregate : undefined;
  return {
    targetEntity: values.targetEntity,
    fieldPath: values.fieldPath?.trim(),
    conditionType: values.conditionType,
    expectedValue: buildExpectedValue(values.conditionType, values.expectedValue, values.valueType ?? "string", aggregate),
    ...(aggregate ? { aggregate } : {}),
    description: values.description?.trim() || undefined,
    active: values.active ?? true,
  };
};

/** Prefill the form from a stored rule. */
export const ruleToFormValues = (
  rule: RuleFields & { description?: string; active?: boolean }
): RuleFormValues => ({
  targetEntity: rule.targetEntity,
  fieldPath: rule.fieldPath,
  aggregate: rule.aggregate,
  conditionType: rule.conditionType,
  description: rule.description,
  active: rule.active ?? true,
  ...expectedValueToForm(rule),
});

/** A fresh rule. */
export const emptyRuleFormValues = (): Partial<RuleFormValues> => ({ active: true, valueType: "string" });
